/**
 * Subtle scroll-triggered reveal for the major homepage sections.
 *
 * Progressive enhancement only: the CSS in snippets/scroll-reveal-styles.liquid
 * only hides content once `html` carries `[data-scroll-ready]`, and that
 * attribute is only ever added here, after IntersectionObserver support is
 * confirmed and reduced-motion is checked. If this script fails to load or
 * run for any reason, every section stays at its normal, fully visible,
 * fully laid-out state - nothing in the page ever depends on JS to become
 * visible.
 *
 * ---------------------------------------------------------------------------
 * 2026-08-14 - FIX: sections could stay permanently invisible.
 *
 * Reproduced live on the draft theme: jump the scroll position past a section
 * that was below the fold at load (browser back/forward scroll restoration,
 * an in-page anchor, scrollIntoView, or a fast trackpad flick) and that section
 * stays at `opacity: 0` forever.
 *
 * Cause: a section below the fold at load is handed to IntersectionObserver,
 * which only ever fires when a *threshold is crossed*. If the section goes
 * from "below the viewport" (ratio 0) straight to "above the viewport"
 * (ratio 0) inside a single frame, no threshold is crossed, the callback
 * never runs, and `.is-visible` is never applied. Because the whole reveal
 * (block fade, staggered craft/Trax text, facts-row columns, Ken Burns) keys
 * off `.is-visible`, the content is gone with no way back short of the user
 * scrolling up again.
 *
 * This site makes that far more likely than usual: `html`/`body` are
 * `overflow: hidden` and the real scroller is `.page-wrapper`. Scroll
 * restoration on back-navigation therefore lands *after* this script has run
 * its initial above-the-fold check, so returning to the homepage from a
 * product page reliably left the middle of the page blank.
 *
 * Fix - the benchmark pattern (checked against Miu Miu / Gucci): content is
 * visible by default and the animation is an enhancement that can never be
 * load-bearing. Three additive guarantees, none of which change the look when
 * things work normally:
 *   1. `sweep()` reveals any pending section that is no longer below the fold,
 *      so "scrolled past without intersecting" is caught.
 *   2. `sweep()` is re-run on scroll (capture phase, so it also catches scrolls
 *      inside `.page-wrapper`), on resize, on `pageshow` (bfcache restore), and
 *      at two short delays after load to catch scroll restoration.
 *   3. A last-resort timer reveals anything still pending after 10s, so even a
 *      silently-broken observer cannot leave content permanently hidden.
 *      Revealing below-the-fold content early is invisible to the user; the
 *      only cost is skipping a fade they were never going to see.
 * ---------------------------------------------------------------------------
 */
(function () {
  if (!('IntersectionObserver' in window)) return;

  var reduceMotionQuery = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduceMotionQuery && reduceMotionQuery.matches) return;

  // Section IDs from templates/index.json (matches Shopify's auto-generated
  // `shopify-section-<id>` wrapper). Keep in sync if sections are re-ordered
  // or renamed on the homepage.
  var sectionIds = [
    'section_OhbVrp',
    'collection_list_4pRwyd',
    'product_list_kLLn8d',
    'media_with_content_REm8Na',
    'product_list_mXdThg',
    'section_M8B9qT',
    'media_with_content_oiVgRV',
    'section_5IfLBc',
  ];

  // Shopify wraps JSON-template sections as
  // `shopify-section-{template-scope}__{section_id}` (e.g.
  // `shopify-section-template--12345__collection_list_4pRwyd`), not a bare
  // `shopify-section-{section_id}`. Match by suffix so this keeps working
  // regardless of the template scope prefix, with a plain-ID fallback for
  // section groups that aren't template-scoped.
  var targets = [];
  for (var i = 0; i < sectionIds.length; i++) {
    var el =
      document.querySelector('[id^="shopify-section-"][id$="__' + sectionIds[i] + '"]') ||
      document.getElementById('shopify-section-' + sectionIds[i]);
    if (el) targets.push(el);
  }

  if (!targets.length) return;

  var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  var pending = [];

  targets.forEach(function (el) {
    el.classList.add('scroll-reveal');

    var rect = el.getBoundingClientRect();
    var alreadyVisible = rect.top < viewportHeight * 0.92 && rect.bottom > 0;

    if (alreadyVisible) {
      // Already on screen at load: mark visible immediately so it never
      // flashes hidden once [data-scroll-ready] is set below.
      el.classList.add('is-visible');
    } else {
      pending.push(el);
    }
  });

  // Only now does the CSS hidden/reveal styling switch on, and by this point
  // every currently-visible section already has .is-visible applied.
  document.documentElement.setAttribute('data-scroll-ready', '');

  if (!pending.length) return;

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) reveal(entry.target);
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
  );

  pending.forEach(function (el) {
    observer.observe(el);
  });

  function reveal(el) {
    el.classList.add('is-visible');
    observer.unobserve(el);
    var index = pending.indexOf(el);
    if (index !== -1) pending.splice(index, 1);
    if (!pending.length) teardown();
  }

  // Reveal anything that is no longer below the fold. Covers the case the
  // observer structurally cannot: a section that was skipped over entirely.
  //
  // The 0.92 factor is the same trigger point as the load-time `alreadyVisible`
  // check above and approximates the observer's `-8%` bottom rootMargin, so
  // during ordinary scrolling this fires at the same moment the observer would
  // and the reveal keeps its intended timing. A section that was scrolled past
  // has a negative `top` and so is caught here as well.
  function sweep() {
    if (!pending.length) return;
    var height = window.innerHeight || document.documentElement.clientHeight;
    pending.slice().forEach(function (el) {
      var rect = el.getBoundingClientRect();
      if (rect.top < height * 0.92) reveal(el);
    });
  }

  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      ticking = false;
      sweep();
    });
  }

  function onPageShow() {
    // bfcache restore re-applies the old scroll offset without firing the
    // usual load-time checks.
    sweep();
    window.setTimeout(sweep, 120);
  }

  function teardown() {
    window.removeEventListener('scroll', onScroll, true);
    window.removeEventListener('resize', onScroll);
    window.removeEventListener('pageshow', onPageShow);
    window.clearTimeout(lastResortTimer);
  }

  // `capture: true` matters: scroll events do not bubble, and the real
  // scroller on this site is `.page-wrapper`, not the document.
  window.addEventListener('scroll', onScroll, { passive: true, capture: true });
  window.addEventListener('resize', onScroll);
  window.addEventListener('pageshow', onPageShow);

  // Scroll restoration lands after this script runs; re-check shortly after.
  window.setTimeout(sweep, 100);
  window.setTimeout(sweep, 600);

  // Last resort: never let content stay hidden, whatever went wrong.
  var lastResortTimer = window.setTimeout(function () {
    pending.slice().forEach(reveal);
  }, 10000);
})();
